import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useLayoutVariant } from '../context/LayoutVariantContext';
import {
  LayoutDashboard,
  ArrowLeftRight,
  SendHorizontal,
  BarChart3,
  Wallet,
  Users,
  FileText,
  Info,
  UserCircle,
  Banknote,
  Bell,
  Shield
} from 'lucide-react';
import { cn } from '../lib/utils';

const Sidebar = () => {
  const { user } = useAuth();
  const { isLive } = useEnvironment();
  const { isReference } = useLayoutVariant();

  const isDashboard = (to) => to === '/dashboard';
  const linksDisabled = isLive && user?.kyc_status !== 'approved';

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/accounts', icon: Wallet, label: 'Accounts' },
    { to: '/beneficiaries', icon: UserCircle, label: 'Beneficiaries' },
    { to: '/transactions', icon: ArrowLeftRight, label: 'Transactions' },
    { to: '/payments', icon: SendHorizontal, label: 'Payments' },
    { to: '/money-transfer', icon: Banknote, label: 'Money Transfer' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/notifications', icon: Bell, label: 'Notifications' },
    { to: '/info', icon: Info, label: 'API Info' },
  ];

  const canAccessUserManagement = ['admin', 'merchant_admin', 'manager'].includes(user?.role);
  const canAccessAuditLogs = ['admin', 'merchant_admin'].includes(user?.role); // Merchant Admin only, not Manager/Viewer
  const adminItems = [
    ...(canAccessUserManagement ? [{ to: '/users', icon: Users, label: 'User Management' }] : []),
    ...(canAccessAuditLogs ? [{ to: '/audit-logs', icon: FileText, label: 'Audit Logs' }] : []),
  ];
  const adminItemsFiltered = adminItems;

  const linkClass = ({ isActive }) => {
    if (isReference) {
      return cn(
        'flex items-center gap-3 px-4 py-3 w-full transition-colors',
        isActive
          ? 'bg-[#0B6445] text-white font-semibold hover:bg-[#095b3d] rounded-none'
          : 'text-[#0E1B16] hover:bg-slate-100 hover:text-[#0E1B16] rounded-md'
      );
    }
    return cn(
      'sidebar-link',
      isActive ? 'sidebar-link-active' : ''
    );
  };

  const disabledClass = isReference
    ? 'flex items-center gap-3 px-4 py-3 rounded-md text-slate-400 opacity-60 cursor-not-allowed pointer-events-none select-none'
    : 'sidebar-link opacity-50 cursor-not-allowed pointer-events-none select-none text-slate-400 rounded-lg block';

  return (
    <aside
      className={cn(
        'w-64 min-h-screen fixed left-0 top-16 flex flex-col',
        isReference
          ? 'bg-white border-r border-[#DCE4E0] p-4'
          : 'bg-[#1C472B] text-white border-r border-[#143620]'
      )}
      data-testid="sidebar"
    >
      <nav className={cn('flex-1 overflow-y-auto', !isReference && 'py-6')}>
        <div className={cn(isReference ? 'mb-2' : 'px-4 mb-2')}>
          <span
            className={cn(
              'text-xs font-semibold uppercase tracking-wider',
              isReference ? 'text-[#0E1B16]/50' : 'text-slate-400'
            )}
          >
            Main
          </span>
        </div>
        <ul className={cn(isReference ? 'space-y-2' : 'space-y-0.5')}>
          {navItems.map((item) => {
            const disabled = linksDisabled && !isDashboard(item.to);
            return (
              <li key={item.to}>
                {disabled ? (
                  <span
                    className={disabledClass}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </span>
                ) : (
                  <NavLink
                    to={item.to}
                    className={linkClass}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>

        {user?.role === 'admin' || adminItemsFiltered.length === 0 ? null : (
          <>
            <div className={cn(isReference ? 'mt-6 mb-2' : 'px-4 mt-8 mb-2')}>
              <span className={cn('text-xs font-semibold uppercase tracking-wider', isReference ? 'text-[#0E1B16]/50' : 'text-slate-400')}>
                Admin
              </span>
            </div>
            <ul className={cn(isReference ? 'space-y-2' : 'space-y-0.5')}>
              {adminItemsFiltered.map((item) => {
                const disabled = linksDisabled;
                return (
                  <li key={item.to}>
                    {disabled ? (
                      <span
                        className={disabledClass}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                        <span>{item.label}</span>
                      </span>
                    ) : (
                      <NavLink
                        to={item.to}
                        className={linkClass}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                        <span>{item.label}</span>
                      </NavLink>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
